import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { ClerkWrapper } from 'components';
import { CircularLoader, FormControl, LoadingButton } from 'components/common';
import { brokerApi } from 'api/broker';
import { BROKER_ONBOARDING_QUERY_KEY } from 'queries/broker/useBrokerOnboarding/useBrokerOnboarding';
import { useBrokerFirm } from 'queries/broker';
import { AddressFields, AddressFieldPaths } from '../CreateOrganisation/AddressFields';
import { mapBackendAddressToForm } from '../CreateOrganisation/mappers';
import { EMPTY_ORGANISATION_ADDRESS, OrganisationAddressForm } from '../CreateOrganisation/types/address';

interface BrokerAddressFormFields {
  sameAsFirmAddress: boolean | null;
  isAddressManual: boolean;
  address: OrganisationAddressForm;
}

const sameAsFirmOptions = [
  { labelKey: 'auth.signup.brokerAddress.sameAsFirm', value: true },
  { labelKey: 'auth.signup.brokerAddress.differentAddress', value: false },
];

const BROKER_ADDRESS_FIELD_PATHS: AddressFieldPaths = {
  addressLine1: 'address.addressLine1',
  addressLine2: 'address.addressLine2',
  postcode: 'address.postcode',
  town: 'address.town',
  county: 'address.county',
  country: 'address.country',
};

function FirmAddressPreview({ address }: { address: OrganisationAddressForm }) {
  const lines = [
    address.addressLine1,
    address.addressLine2,
    address.town,
    address.county,
    address.postcode,
    address.country,
  ].filter(Boolean);

  return (
    <div className='mt-4 rounded border border-gray-200 bg-gray-50 p-4 text-sm'>
      <p className='mb-2 font-medium text-gray-700'>
        <FormattedMessage id='auth.signup.brokerAddress.firmAddressPreview' />
      </p>
      {lines.map(line => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

export function BrokerAddress() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: firm, isLoading: isFirmLoading } = useBrokerFirm();
  const hasFirmAddress = !!firm?.address;
  const firmAddressForm = firm?.address ? mapBackendAddressToForm(firm.address) : null;
  const requiredMsg = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<BrokerAddressFormFields>({
    defaultValues: {
      sameAsFirmAddress: null,
      isAddressManual: false,
      address: EMPTY_ORGANISATION_ADDRESS,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const sameAsFirmAddress = useWatch({ name: 'sameAsFirmAddress', control: form.control });

  useEffect(() => {
    if (!isFirmLoading && !hasFirmAddress) {
      form.setValue('sameAsFirmAddress', false);
    }
  }, [isFirmLoading, hasFirmAddress, form]);

  const handleSameAsFirmChange = useCallback(() => {
    form.setValue('isAddressManual', false);
    form.setValue('address', { ...EMPTY_ORGANISATION_ADDRESS });
  }, [form]);

  const onSubmit = useCallback(
    async (formValues: BrokerAddressFormFields) => {
      setIsSubmitting(true);
      try {
        const useFirmAddress = hasFirmAddress && formValues.sameAsFirmAddress === true;

        if (useFirmAddress) {
          await brokerApi.patchBroker({ sameAsFirmAddress: true });
        } else {
          await brokerApi.patchBroker({
            sameAsFirmAddress: false,
            address: {
              addressLine1: formValues.address.addressLine1,
              addressLine2: formValues.address.addressLine2 || undefined,
              postcode: formValues.address.postcode,
              town: formValues.address.town,
              county: formValues.address.county || undefined,
              country: formValues.address.country,
            },
          });
        }

        await queryClient.invalidateQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY] });
        await queryClient.refetchQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY], type: 'active' });

        enqueueSnackbar(intl.formatMessage({ id: 'auth.signup.brokerAddress.success' }), { variant: 'info' });
      } catch (e) {
        Sentry.captureException(e);
        enqueueSnackbar(intl.formatMessage({ id: 'common.error.somethingWrong' }), { variant: 'error' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [queryClient, enqueueSnackbar, intl, hasFirmAddress],
  );

  const showAddressForm = !hasFirmAddress || sameAsFirmAddress === false;
  const showConfirmButton = !hasFirmAddress || sameAsFirmAddress !== null;

  return (
    <ClerkWrapper width='xl'>
      <FormProvider {...form}>
        <h2 className='text-md mb-4 w-full text-center font-semibold'>
          <FormattedMessage id='auth.signup.brokerAddress.title' />
        </h2>
        <p className='mb-6 text-center text-sm text-gray-500'>
          <FormattedMessage id='auth.signup.brokerAddress.subtitle' />
        </p>
        <form onSubmit={form.handleSubmit(onSubmit)} className='w-full max-w-xl px-2 sm:px-4'>
          {isFirmLoading ? (
            <div className='flex justify-center py-8'>
              <CircularLoader />
            </div>
          ) : (
            <>
              {hasFirmAddress && (
                <FormControl
                  name='sameAsFirmAddress'
                  rules={{
                    validate: (v: unknown): true | string => {
                      if (v === true || v === false) {
                        return true;
                      }
                      return requiredMsg;
                    },
                  }}
                  columnsAmount={1}
                  isWide
                  onChangeEffect={handleSameAsFirmChange}
                  radioOptions={sameAsFirmOptions}
                />
              )}

              {sameAsFirmAddress === true && firmAddressForm && <FirmAddressPreview address={firmAddressForm} />}

              {showAddressForm && (
                <div className={hasFirmAddress ? 'mt-4' : undefined}>
                  <AddressFields
                    fieldPaths={BROKER_ADDRESS_FIELD_PATHS}
                    manualTogglePath='isAddressManual'
                    addressValuePath='address'
                  />
                </div>
              )}

              {showConfirmButton && (
                <div className='mt-6 flex w-full justify-end'>
                  <LoadingButton
                    isLoading={isSubmitting}
                    buttonProps={{ type: 'submit', variant: 'contained', className: 'h-9 min-w-32' }}
                  >
                    <FormattedMessage id='cta.confirm' />
                  </LoadingButton>
                </div>
              )}
            </>
          )}
        </form>
      </FormProvider>
    </ClerkWrapper>
  );
}
